import { decodeJwt } from '@/utilities';

const SESSION_KEY = 'acv-registration-session';

const REQUIRED_PARTIAL_TOKEN_CLAIMS = [
  'acv-user-id',
  'acv-identity-id',
  'auction-access-person-id',
];

// Claims only present once getSteppedUpSessionTokenForExistingUser has actually succeeded and
// returned a dealer-scoped token - the dealer form submission needs all four.
const REQUIRED_STEP_UP_CLAIMS = [
  'acv-user-id',
  'auction-access-person-id',
  'acv-organization-id',
  'auction-access-dealer-id',
];

// Without this check, a stale token for dealer A would be accepted as the token for dealer B.
const isScopedToSelectedDealer = (claims, getters) => {
  const { auctionAccessId: selectedDealerId } = getters.selectedDealer ?? {};
  return String(claims['auction-access-dealer-id']) === String(selectedDealerId);
};

const registrationSession = {
  state: {
    registrationToken: sessionStorage.getItem(SESSION_KEY) ?? null,
    sessionExpiredMessage: null,
  },
  mutations: {
    setRegistrationToken(state, token) {
      if (!token) return;
      state.registrationToken = token;
      sessionStorage.setItem(SESSION_KEY, token);
    },
    clearRegistrationToken(state) {
      state.registrationToken = null;
      sessionStorage.removeItem(SESSION_KEY);
    },
    setSessionExpiredMessage(state, message) {
      state.sessionExpiredMessage = message;
    },
    clearSessionExpiredMessage(state) {
      state.sessionExpiredMessage = null;
    },
  },
  actions: {
    setRegistrationToken({ commit }, token) {
      commit('setRegistrationToken', token);
    },
    clearRegistrationToken({ commit }) {
      commit('clearRegistrationToken');
    },
    setSessionExpiredMessage({ commit }, message) {
      commit('setSessionExpiredMessage', message);
    },
    clearSessionExpiredMessage({ commit }) {
      commit('clearSessionExpiredMessage');
    },
  },
  getters: {
    registrationToken: (state) => state.registrationToken,
    hasRegistrationToken: (state) => !!state.registrationToken,
    sessionExpiredMessage: (state) => state.sessionExpiredMessage,
    auctionAccessPersonId: (state) => {
      const claims = decodeJwt(state.registrationToken) ?? {};
      return claims['auction-access-person-id'] ?? null;
    },
    // A partial token predates dealer selection, so it carries no auction-access-dealer-id
    // claim to scope - unlike hasSteppedUp, this must not call isScopedToSelectedDealer.
    hasPartialToken: (state) => {
      const claims = decodeJwt(state.registrationToken) ?? {};

      return REQUIRED_PARTIAL_TOKEN_CLAIMS.every((claim) => Boolean(claims[claim]));
    },
    hasSteppedUp: (state, getters) => {
      const claims = decodeJwt(state.registrationToken) ?? {};

      const hasAllClaims = REQUIRED_STEP_UP_CLAIMS.every((claim) => Boolean(claims[claim]));
      if (!hasAllClaims) return false;

      return isScopedToSelectedDealer(claims, getters);
    },
  },
};

export default registrationSession;
